import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useTenantLabels, useTenantLabelsReady } from "../../lib/labels";
import { useT, t as i18nT } from "../../lib/i18n";
import { SegmentedControl, alpha, Avatar, Btn, Card, Icon, Pill, Skeleton, T, tx } from "@v0y4ge/jrni-ui";
import { getRevenuePacing, getDashboardTrends, getConversionFunnel, getCustomerBehaviorMix, getRegistrationChannels, getDashboardCommandCenter } from "@v0y4ge/api-client";
import type { RevenuePacingResponse, DashboardTrendsResponse, ConversionFunnelResponse, CustomerBehaviorMixResponse, RegistrationChannelsResponse, DashboardCommandCenterResponse } from "@v0y4ge/api-contracts";
import { useNav } from "../../shell/router";
import { StatTile } from "../../shell/composites";
import { LiveFromEventDay } from "../../shell/list-composites";
import type { UIEvent } from "../../lib/events-adapter";
import { isToday as _isToday, currentCalendarDate, getDateTimeConfig } from "../../lib/datetime";

/* ── Helpers ────────────────────────────────────────────────────────── */
export function isEventToday(ev: UIEvent): boolean {
  return _isToday(ev.raw?.startsAt, ev.raw?.timezone || undefined);
}
export function formatToday(): string {
  return new Intl.DateTimeFormat(getDateTimeConfig().locale, { weekday: "long", month: "long", day: "numeric" }).format(new Date());
}
export function formatTodayShort(): string {
  return new Intl.DateTimeFormat(getDateTimeConfig().locale, { weekday: "short", month: "short", day: "numeric" }).format(new Date());
}

/** Locale-correct calendar labels via Intl (no hardcoded English month/weekday
 *  names) — anchored on known dates (2000-01-01/2023-01-01 is a Sunday) so the
 *  formatter only ever supplies the label for the requested unit. */
function monthLongLabel(month: number): string {
  return new Intl.DateTimeFormat(getDateTimeConfig().locale, { month: "long" }).format(new Date(2000, month, 1));
}
function weekdayNarrowLabels(): string[] {
  const fmt = new Intl.DateTimeFormat(getDateTimeConfig().locale, { weekday: "narrow" });
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2023, 0, 1 + i)));
}

/* ── SectionHeader ──────────────────────────────────────────────────── */
export function SectionHeader({ title, subtitle }: { title: ReactNode; subtitle?: ReactNode }) {
  return (
    <div style={{ marginBottom: 10, display: "flex", alignItems: "baseline", gap: 10 }}>
      <h2 style={{ ...tx.h3, fontWeight: 700, margin: 0 }}>{title}</h2>
      {subtitle ? <span style={{ fontSize: 12, color: T.soft }}>{subtitle}</span> : null}
    </div>
  );
}

/* ── CollapsibleRightRail ───────────────────────────────────────────── */
export function CollapsibleRightRail({ width = 300, children }: { width?: number; children: ReactNode }) {
  const t = useT(); // i18n pilot (kit v4.2): dashboard.components.* keys
  const [expanded, setExpanded] = useState(() => { try { return localStorage.getItem("rightRailExpanded") !== "0"; } catch { return true; } });
  const toggle = () => setExpanded((v) => { const next = !v; try { localStorage.setItem("rightRailExpanded", next ? "1" : "0"); } catch {} return next; });
  return (
    <aside aria-label={t("dashboard.components.ariaLabel", "Secondary navigation")} style={{ width: expanded ? width : 44, flexShrink: 0, background: T.white, borderLeft: `1px solid ${T.border}`, display: "flex", flexDirection: "column", transition: "width 180ms cubic-bezier(0.4,0,0.2,1)", overflow: "hidden" }}>
      {/* ui-raw: borderless flat panel-toggle with a variable justifyContent (flex-start
          expanded / center collapsed) and a hover-highlight handled via onMouseEnter/Leave.
          IconButton's fixed chrome (1px border + shadowChrome, always-centered) can't
          reproduce this borderless flat look without adding a border/shadow that isn't here. */}
      <button onClick={toggle} aria-label={expanded ? t("dashboard.dashboardComponents.collapsePanel", "Collapse panel") : t("dashboard.dashboardComponents.expandPanel", "Expand panel")} style={{ height: 36, margin: "8px 6px 4px", borderRadius: 6, border: "none", background: "transparent", color: T.n2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: expanded ? "flex-start" : "center", gap: 8, padding: "0 8px", flexShrink: 0 }}
        onMouseEnter={(e) => (e.currentTarget.style.background = T.n6)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
        <Icon.chev dir={expanded ? "right" : "left"} size={14} />
      </button>
      <div style={{ flex: 1, overflow: "auto", padding: expanded ? "8px 16px 16px" : 0, display: "flex", flexDirection: "column", gap: 16, opacity: expanded ? 1 : 0, pointerEvents: expanded ? "auto" : "none", transition: "opacity 140ms" }}>
        {expanded ? children : null}
      </div>
    </aside>
  );
}

/* ── MiniCalendar ───────────────────────────────────────────────────── */
// ui-raw: the two mini-calendar nav buttons below use this 22×22 chip style.
// IconButton's default is 36×36 with a T.n4 border + T.shadowChrome baked into
// its fixed style object (which fully replaces any caller `style`, so neither
// the smaller size, the T.border color, nor the absence of a shadow can be
// reproduced) — adopting it would visibly add a shadow and resize the chip.
const miniNavStyle: CSSProperties = { width: 22, height: 22, borderRadius: 4, border: `1px solid ${T.border}`, background: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.n1 };
export function MiniCalendar({ eventDays }: { eventDays?: number[] }) {
  const nav = useNav();
  const t = useT();
  // B2 (2026-07-22 docket): the mini-calendar's "today" follows the tenant
  // display tz (was browser-local `new Date()`), matching the dashboard.
  const today = currentCalendarDate();
  const [view, setView] = useState({ year: today.y, month: today.mo });
  const first = new Date(view.year, view.month, 1);
  const last = new Date(view.year, view.month + 1, 0);
  const firstDow = first.getDay();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const step = (dir: number) => setView((v) => { let m = v.month + dir, y = v.year; if (m < 0) { m = 11; y -= 1; } if (m > 11) { m = 0; y += 1; } return { year: y, month: m }; });
  const todayDate = view.year === today.y && view.month === today.mo ? today.d : 0;
  const weekdayLabels = weekdayNarrowLabels();
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{monthLongLabel(view.month)} {view.year}</span>
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={() => step(-1)} title={t("dashboard.components.title", "Previous month")} style={miniNavStyle}><Icon.chev dir="left" size={12} /></button>
          <button onClick={() => step(1)} title={t("dashboard.components.title2", "Next month")} style={miniNavStyle}><Icon.chev dir="right" size={12} /></button>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, fontSize: 11, color: T.soft, marginBottom: 4 }}>
        {weekdayLabels.map((d, i) => <div key={i} style={{ textAlign: "center" }}>{d}</div>)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const isToday = d === todayDate;
          const hasEvent = eventDays?.includes(d);
          return (
            <div key={i} onClick={() => nav.go("calendar.day", { day: d, month: view.month, year: view.year })} title={i18nT("dashboard.components.monthsViewMonthD.icu", "{value0} {value1}", { value0: monthLongLabel(view.month), value1: d })}
              style={{ aspectRatio: "1", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: isToday ? 700 : 500, color: isToday ? T.onColor : T.n1, background: isToday ? T.brandFill : "transparent", borderRadius: 999, position: "relative", cursor: "pointer" }}
              onMouseEnter={(e) => { if (!isToday) e.currentTarget.style.background = T.n6; }}
              onMouseLeave={(e) => { if (!isToday) e.currentTarget.style.background = "transparent"; }}>
              {d}
              {hasEvent && !isToday ? <span style={{ position: "absolute", bottom: 2, width: 4, height: 4, borderRadius: 999, background: T.p1 }} /> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── LiveOpsCard ─────────────────────────────────────────────────────── */
const LIVE_OPS_KEY = "dashLiveOpsOpen";
export function LiveOpsCard({ events }: { events: UIEvent[] }) {
  const nav = useNav();
  const t = useT();
  const live = events.filter((e) => e.status === "live");
  const [open, setOpen] = useState(() => { try { return localStorage.getItem(LIVE_OPS_KEY) !== "0"; } catch { return true; } });
  // drop24 sweep: the check-in sparkline was a hardcoded numeric series. Bind it
  // to the real command-center check-in pace; hide the chart when there's no data.
  const [pace, setPace] = useState<number[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (typeof getDashboardCommandCenter !== "function") return;
    getDashboardCommandCenter({ baseUrl: PACING_API_BASE } as never)
      .then((r: unknown) => { if (cancelled) return; const raw = r as Record<string, any>; const cc = (raw?.data ?? raw) as DashboardCommandCenterResponse; const pts = Array.isArray(cc?.checkInPace) ? cc.checkInPace.map((b) => Number(b.value) || 0) : []; setPace(pts); })
      .catch((e: unknown) => console.warn("getDashboardCommandCenter (LiveOps pace) failed:", e));
    return () => { cancelled = true; };
  }, []);
  if (live.length === 0) return null;
  const toggle = () => setOpen((v) => { const next = !v; try { localStorage.setItem(LIVE_OPS_KEY, next ? "1" : "0"); } catch {} return next; });
  const primary = live[0];
  const others = live.slice(1);
  const checkRate = primary.registered ? Math.round((primary.checked / primary.registered) * 100) : 0;
  const capPct = primary.capacity ? Math.round((primary.registered / primary.capacity) * 100) : 0;
  const paceMax = pace.length ? Math.max(...pace, 1) : 1;
  return (
    <Card>
      <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: open ? `1px solid ${T.border}` : "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: T.green1, boxShadow: `0 0 0 4px ${T.green2}` }} />
          <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("dashboard.components.text", "Live now")}</h3>
          <Pill tone="success" dot>{live.length} {t("dashboard.components.live", "live")}</Pill>
          <LiveFromEventDay note={t("dashboard.dashboardComponents.liveOpsSyncNoteMyDay", "Check-ins, walk-ups and pass scans stream live from My Day on the floor.")} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Btn variant="link" onClick={() => nav.go("checkins.today")}>{t("dashboard.components.text2", "Live check-ins →")}</Btn>
          {/* ui-raw: 28×28 collapse chip (T.border, T.soft icon, no shadow) plus an
              inner rotating chevron span. IconButton's fixed 36×36/T.n4-border/
              shadowChrome style fully replaces caller `style` (can't shrink it,
              swap the border/icon color, or drop the shadow), so this stays raw. */}
          <button onClick={toggle} aria-expanded={open} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: T.white, color: T.soft, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ display: "inline-flex", transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 120ms" }}><Icon.chev dir="down" size={14} /></span>
          </button>
        </div>
      </div>
      {open ? (
        <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 24, alignItems: "start" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <span style={{ width: 32, height: 32, borderRadius: 8, background: primary.hue, flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{primary.title}</div>
                  <div style={{ fontSize: 12, color: T.soft }}>{primary.location}</div>
                </div>
              </div>
              <Btn variant="secondary" size="sm" onClick={() => nav.openEvent(primary)}>{t("dashboard.components.text3", "Open")}</Btn>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
              {[{ label: t("dashboard.components.registered", "Registered"), value: String(primary.registered), sub: t("dashboard.dashboardComponents.ofCapacityAbbrev.icu", "of {capacity} cap", { capacity: primary.capacity }) },
                { label: t("dashboard.components.checkedIn", "Checked in"), value: String(primary.checked), sub: t("dashboard.dashboardComponents.checkedInPct.icu", "{pct}% in", { pct: checkRate }) },
                { label: t("dashboard.components.waitlist", "Waitlist"), value: String(primary.waitlist), sub: primary.waitlist > 0 ? t("dashboard.dashboardComponents.readyToPromote", "ready to promote") : t("dashboard.dashboardComponents.clear", "clear") },
                { label: t("dashboard.components.capacity", "Capacity"), value: `${capPct}%`, sub: primary.registered >= primary.capacity ? t("dashboard.dashboardComponents.atCapacity", "at capacity") : t("dashboard.dashboardComponents.filling", "filling") }
              ].map((stat, i) => (
                <div key={i}>
                  <div style={{ fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.04, fontWeight: 600 }}>{stat.label}</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: T.n1, marginTop: 4, letterSpacing: -0.5 }}>{stat.value}</div>
                  <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{stat.sub}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: T.soft, marginBottom: 8 }}>{t("dashboard.components.text4", "Check-ins · recent pace")}</div>
            {pace.length ? (<>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 64 }}>
              {pace.map((h, i) => <div key={i} style={{ flex: 1, height: `${(h / paceMax) * 100}%`, minHeight: 2, background: i >= pace.length - 2 ? T.p1 : T.p3, borderRadius: 2 }} />)}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 10, color: T.soft }}>
              <span>{t("dashboard.components.earlier", "earlier")}</span><span>{t("dashboard.components.now", "now")}</span>
            </div>
            </>) : (
            <div style={{ height: 64, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: T.soft }}>{t("dashboard.components.text5", "No recent check-in pace.")}</div>
            )}
            {others.length > 0 ? (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: 6 }}>
                {others.map((ev) => (
                  <div key={ev.id} onClick={() => nav.openEvent(ev)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12 }}>
                    <span style={{ width: 6, height: 6, borderRadius: 999, background: ev.hue }} />
                    <span style={{ flex: 1, color: T.n1, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.title}</span>
                    <span style={{ color: T.soft }}>{ev.checked}/{ev.registered}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </Card>
  );
}

/* ── PacingSection ───────────────────────────────────────────────────── */
const PACING_API_BASE = (typeof window !== "undefined" && (window as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__) || (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_API_URL) || "";

export function PacingSection({ events }: { events: UIEvent[] }) {
  const nav = useNav();
  const t = useT();
  // #getRevenuePacing (packet #5) + at-risk join (packet #6 #689): goal/actual/regGoal/elapsed AND
  // the at-risk list all come from one RevenuePacingResponse, so everything the card renders stays
  // internally consistent. The at-risk rows join pacing.atRiskEvents (eventId/daysLeft/fillPct) against
  // the `events` prop for the title/hue/registered/capacity the rows display.
  const [pacing, setPacing] = useState<RevenuePacingResponse | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (typeof getRevenuePacing === "function") {
      getRevenuePacing({ baseUrl: PACING_API_BASE } as never).then((r: unknown) => {
        if (cancelled) return;
        // Tolerate an envelope ({ data: {...} } / { pacing: {...} }) and require the
        // numeric fields — a resolved-but-shapeless response must NOT pass the
        // skeleton guard (it crashed on .toLocaleString of undefined).
        const raw = r as Record<string, any>;
        const p = (raw?.pacing ?? raw?.data ?? raw) as Record<string, any>;
        if (p && typeof p.goalMinor === "number" && typeof p.regGoal === "number") setPacing(p as RevenuePacingResponse);
        else console.warn("getRevenuePacing returned an unexpected shape — keeping the pacing skeleton:", raw);
      }).catch((e: unknown) => console.warn("getRevenuePacing failed:", e));
    }
    return () => { cancelled = true; };
  }, []);

  // #689 NaN-flash guard: before the async pacing response lands, every ratio below is 0/0 = NaN
  // (rendered as "$NaNk" / "NaN% vs goal"). Early-return a skeleton until pacing resolves — this also
  // keeps the at-risk card from rendering before pacing.atRiskEvents exists.
  if (pacing === null) {
    return (
      <div>
        <SectionHeader title={t("dashboard.components.title3", "Targets & pacing")} subtitle={t("dashboard.components.subtitle", "Quarter to date")} />
        <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("dashboard.components.text6", "Loading pacing…")}</div>
      </div>
    );
  }

  const goal = pacing.goalMinor / 100;
  const actual = pacing.actualMinor / 100;
  const regGoal = pacing.regGoal;
  const regActual = pacing.regActual;
  const elapsed = pacing.periodElapsedPct / 100;
  const expected = goal * elapsed;
  const projected = elapsed > 0 ? Math.round(actual / elapsed) : 0;
  const projDelta = goal > 0 ? ((projected - goal) / goal) * 100 : 0;
  const fillG = goal > 0 ? actual / goal : 0;
  const regPct = regActual / Math.max(regGoal, 1);
  const ahead = actual >= expected;
  const fmtMoney = (n: number) => { const k = n / 1000; return `$${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`; };
  // #689: real at-risk events joined by eventId from pacing.atRiskEvents (no synthetic dayCycle).
  // An upcoming under-filled event with no matching atRiskEvents entry does not appear (no fallback).
  const atRiskById = new Map((pacing.atRiskEvents ?? []).map((a) => [a.eventId, a] as const));
  const atRisk = events
    .map((e) => ({ e, real: atRiskById.get(e.id) }))
    .filter((x): x is { e: UIEvent; real: NonNullable<typeof x.real> } => Boolean(x.real))
    .map(({ e, real }) => ({ ...e, fill: real.fillPct / 100, daysLeft: real.daysLeft }))
    .sort((a, b) => a.fill - b.fill)
    .slice(0, 4);
  return (
    <div>
      <SectionHeader title={t("dashboard.components.title3", "Targets & pacing")} subtitle={i18nT("dashboard.components.tDashboardComponentsSubtitleMathRoundElapsed100Elapsed.icu", "{value0} · {value1}% elapsed", { value0: t("dashboard.components.subtitle", "Quarter to date"), value1: Math.round(elapsed * 100) })} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.25fr", gap: 16 }}>
        <Card>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("dashboard.components.text7", "Revenue pacing")}</h3>
            <Pill tone={ahead ? "success" : "warn"} dot>{ahead ? t("dashboard.dashboardComponents.aheadOfPace", "Ahead of pace") : t("dashboard.dashboardComponents.behindPace", "Behind pace")}</Pill>
          </div>
          <div style={{ padding: "16px 20px" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 30, fontWeight: 700, color: T.n1, letterSpacing: -0.5 }}>{fmtMoney(actual)}</span>
              <span style={{ fontSize: 13, color: T.soft }}>{t("dashboard.components.of", "of")}{" "}{fmtMoney(goal)} {t("dashboard.components.goal", "goal")}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: ahead ? T.green1 : T.red1, marginBottom: 16 }}>
              {ahead ? "↑" : "↓"} {t("dashboard.components.text8", "Projected")}{" "}{fmtMoney(projected)} · {projDelta >= 0 ? "+" : ""}{projDelta.toFixed(1)}{t("dashboard.components.text9", "% vs goal")}
            </div>
            <div style={{ position: "relative", height: 14, background: T.n6, borderRadius: 999, marginBottom: 6, overflow: "visible" }}>
              <div style={{ position: "absolute", inset: 0, borderRadius: 999, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.min(100, fillG * 100)}%`, background: ahead ? T.green1 : T.orange1, borderRadius: 999 }} />
              </div>
              <div style={{ position: "absolute", top: -4, bottom: -4, left: `${elapsed * 100}%`, width: 2, background: T.n1, borderRadius: 2 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.soft, marginBottom: 16 }}>
              <span>{Math.round(fillG * 100)}{t("dashboard.components.text10", "% of goal")}</span><span>{t("dashboard.components.text11", "Expected:")}{" "}{Math.round(elapsed * 100)}%</span>
            </div>
            <div style={{ paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: T.soft, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.04 }}>{t("dashboard.components.text12", "Registrations")}</span>
                <span style={{ fontSize: 13, color: T.n1 }}><strong>{regActual.toLocaleString()}</strong> / {regGoal.toLocaleString()} ({Math.round(regPct * 100)}%)</span>
              </div>
              <div style={{ position: "relative", height: 8, background: T.n6, borderRadius: 999 }}>
                <div style={{ position: "absolute", inset: 0, borderRadius: 999, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, regPct * 100)}%`, background: T.tDarkBlue, borderRadius: 999 }} />
                </div>
                <div style={{ position: "absolute", top: -3, bottom: -3, left: `${elapsed * 100}%`, width: 2, background: T.n1, borderRadius: 2 }} />
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("dashboard.components.text13", "At-risk events")}</h3>
              {atRisk.length > 0 ? <Pill tone="warn">{atRisk.length} {t("dashboard.components.underPacing", "under-pacing")}</Pill> : null}
            </div>
            <Btn variant="link" onClick={() => nav.go("events")}>{t("dashboard.components.text14", "All events →")}</Btn>
          </div>
          {atRisk.length === 0 ? (
            <div style={{ padding: 28, textAlign: "center" }}>
              <div style={{ fontSize: 13, color: T.green1, fontWeight: 600 }}>{t("dashboard.components.text15", "✓ All upcoming events are pacing on target")}</div>
              <div style={{ fontSize: 12, color: T.soft, marginTop: 4 }}>{t("dashboard.components.text16", "Nothing needs a boost right now.")}</div>
            </div>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {atRisk.map((e, i) => {
                const urgent = e.daysLeft <= 7 && e.fill < 0.5;
                const fillPct = Math.round(e.fill * 100);
                return (
                  <li key={e.id} onClick={() => nav.openEvent(e)} style={{ padding: "12px 16px", display: "grid", gridTemplateColumns: "1fr 92px", alignItems: "center", gap: 12, borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 3, background: e.hue, flexShrink: 0 }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.title}</span>
                      </div>
                      <div style={{ fontSize: 12, color: urgent ? T.red1 : T.soft, marginTop: 2, fontWeight: urgent ? 600 : 400 }}>{t("dashboard.components.in", "in")}{" "}{e.daysLeft}{t("dashboard.components.text17", "d ·")} {e.registered}/{e.capacity} {t("dashboard.components.registeredb1a9e561", "registered")}</div>
                    </div>
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}><span style={{ color: T.soft }}>{t("dashboard.components.fill", "fill")}</span><span style={{ color: T.n1, fontWeight: 600 }}>{fillPct}%</span></div>
                      <div style={{ height: 6, background: T.n6, borderRadius: 999, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${Math.max(4, fillPct)}%`, background: urgent ? T.red1 : T.orange1, borderRadius: 999 }} />
                      </div>
                    </div>
                    {/* (CC, 2026-10-05) Boost/Remind removed: it opened campaigns.email, which is not a registered route. */}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

/* ── PerformanceSection ─────────────────────────────────────────────── */
// drop24 punt-back: trend + funnel were FABRICATED (hardcoded 10-week series;
// funnel = registrations × invented ratios). Both now bind to real ops —
// getDashboardTrends({ metric, weeks }) + getConversionFunnel() — rendering
// loading / error / "—" honestly rather than synthesizing any stage.
export function PerformanceSection(_props: { events: UIEvent[] }) {
  const nav = useNav();
  const t = useT();
  const [metric, setMetric] = useState<"registrations" | "attendance" | "revenue">("registrations");
  const [trends, setTrends] = useState<DashboardTrendsResponse | null>(null);
  const [trendsErr, setTrendsErr] = useState(false);
  const [funnel, setFunnel] = useState<ConversionFunnelResponse | null>(null);
  const [funnelErr, setFunnelErr] = useState(false);
  const META: Record<string, { label: string; color: string; money: boolean }> = {
    registrations: { label: t("dashboard.components.text12", "Registrations"), color: T.tDarkBlue, money: false },
    attendance: { label: t("dashboard.components.attendance", "Attendance"), color: T.p1, money: false },
    revenue: { label: t("dashboard.components.revenue", "Revenue"), color: T.green1, money: true },
  };

  useEffect(() => {
    let cancelled = false; setTrends(null); setTrendsErr(false);
    if (typeof getDashboardTrends !== "function") { setTrendsErr(true); return; }
    getDashboardTrends({ baseUrl: PACING_API_BASE, metric, weeks: 10 } as never)
      .then((r: unknown) => { if (cancelled) return; const raw = r as Record<string, any>; const p = (raw?.data ?? raw) as DashboardTrendsResponse; if (p && Array.isArray(p.series)) setTrends(p); else { console.warn("getDashboardTrends unexpected shape:", raw); setTrendsErr(true); } })
      .catch((e: unknown) => { if (!cancelled) { console.warn("getDashboardTrends failed:", e); setTrendsErr(true); } });
    return () => { cancelled = true; };
  }, [metric]);

  useEffect(() => {
    let cancelled = false;
    if (typeof getConversionFunnel !== "function") { setFunnelErr(true); return; }
    getConversionFunnel({ baseUrl: PACING_API_BASE } as never)
      .then((r: unknown) => { if (cancelled) return; const raw = r as Record<string, any>; const p = (raw?.data ?? raw) as ConversionFunnelResponse; if (p && typeof p.registered === "number") setFunnel(p); else { console.warn("getConversionFunnel unexpected shape:", raw); setFunnelErr(true); } })
      .catch((e: unknown) => { if (!cancelled) { console.warn("getConversionFunnel failed:", e); setFunnelErr(true); } });
    return () => { cancelled = true; };
  }, []);

  const meta = META[metric]!;
  const series = trends?.series ?? [];
  const fmt = (n: number) => meta.money ? `$${(n / 1000).toFixed(1)}k` : n.toLocaleString();
  const cur = series.length ? series[series.length - 1]!.value : null;
  const wow = trends?.deltaWoWPct ?? null;
  const win = trends?.deltaWindowPct ?? null;
  const sMax = series.length ? Math.max(...series.map((p) => p.value), 1) : 1;

  const views = funnel?.pageViews ?? null;
  const reg = funnel?.registered ?? null;
  const attended = funnel?.checkedIn ?? null;
  /* The server's no-show figure, over ENDED events only (CC 0478). Never derived from registered - checkedIn: a future
     event's guests haven't had the chance to check in. A null rate (no ended event in the window) reads "—". */
  const noShowRate = funnel?.noShow?.ratePct ?? null;
  const noShow = funnel?.noShow ? funnel.noShow.noShows : null;
  const pctOf = (a: number | null, b: number | null) => (a != null && b != null && b > 0) ? `${Math.round(a / b * 100)}%` : "—";
  const fmtN = (n: number | null) => n == null ? "—" : n.toLocaleString();
  const barW = (n: number | null) => (n != null && views != null && views > 0) ? Math.max(8, (n / views) * 100) : 0;
  const funnelStages = [
    { label: t("dashboard.components.pageViews", "Page views"), count: views, color: T.tLightBlue, sub: t("dashboard.components.funnelSubPageViews", "Landing-page visitors"), conv: null as string | null },
    { label: t("dashboard.components.text12", "Registrations"), count: reg, color: T.tDarkBlue, sub: t("dashboard.components.funnelSubRegistrations", "Completed sign-up"), conv: pctOf(reg, views) },
    { label: t("dashboard.components.checkedIn", "Checked in"), count: attended, color: T.p1, sub: t("dashboard.components.funnelSubCheckedIn", "Attended on the day"), conv: pctOf(attended, reg) },
  ];

  return (
    <div>
      <SectionHeader title={t("dashboard.components.title4", "Performance")} subtitle={t("dashboard.components.subtitle2", "Trend & conversion · last 10 weeks")} />
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 16 }}>
        <Card>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("dashboard.components.text18", "Weekly trend")}</h3>
            {/* Metric switcher. The three metric keys are configuration, not data. */}
            <SegmentedControl<"registrations" | "attendance" | "revenue">
              size="sm"
              ariaLabel={t("dashboard.components.ariaLabel2", "Metric")}
              value={metric}
              onChange={setMetric}
              options={(Object.keys(META) as ("registrations" | "attendance" | "revenue")[]).map((k) => ({ value: k, label: META[k].label }))}
            />
          </div>
          <div style={{ padding: "16px 20px" }}>
            {trendsErr ? (
              <div style={{ padding: "28px 8px", textAlign: "center", color: T.soft, fontSize: 13 }}>{t("dashboard.components.text19", "Couldn't load the weekly trend.")}</div>
            ) : trends === null ? (
              <div style={{ padding: "28px 8px", textAlign: "center", color: T.soft, fontSize: 13 }}>{t("dashboard.components.text20", "Loading trend…")}</div>
            ) : series.length === 0 ? (
              <div style={{ padding: "28px 8px", textAlign: "center", color: T.soft, fontSize: 13 }}>{t("dashboard.components.text21", "No trend data yet.")}</div>
            ) : (<>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 30, fontWeight: 700, color: T.n1, letterSpacing: -0.5 }}>{cur != null ? fmt(cur) : "—"}</span>
                {wow != null ? <span style={{ fontSize: 12, fontWeight: 600, color: wow >= 0 ? T.green1 : T.red1 }}>{wow >= 0 ? "↑" : "↓"} {Math.abs(wow).toFixed(1)}{t("dashboard.components.text22", "% WoW")}</span> : null}
                {win != null ? <span style={{ fontSize: 12, color: T.soft }}>· {win >= 0 ? "+" : ""}{win.toFixed(0)}{t("dashboard.components.text23", "% 10wk")}</span> : null}
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 124 }}>
                {series.map((pt, i) => (
                  <div key={i} title={i18nT("dashboard.components.ptWeekFmtPtValue.icu", "{value0}: {value1}", { value0: pt.week, value1: fmt(pt.value) })} style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}>
                    <div style={{ height: `${(pt.value / sMax) * 100}%`, background: i === series.length - 1 ? meta.color : alpha(meta.color, "33"), borderRadius: 3 }} />
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 11, color: T.soft }}><span>{series.length} {t("dashboard.components.text24", "wks ago")}</span><span>{t("dashboard.components.text25", "This week")}</span></div>
            </>)}
          </div>
        </Card>
        <Card>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("dashboard.components.text26", "Conversion funnel")}</h3>
            <Btn variant="link" onClick={() => nav.go("reports.events")}>{t("dashboard.components.text27", "Full report →")}</Btn>
          </div>
          <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
            {funnelErr ? (
              <div style={{ padding: "20px 8px", textAlign: "center", color: T.soft, fontSize: 13 }}>{t("dashboard.components.text28", "Couldn't load the conversion funnel.")}</div>
            ) : funnel === null ? (
              <div style={{ padding: "20px 8px", textAlign: "center", color: T.soft, fontSize: 13 }}>{t("dashboard.components.text29", "Loading funnel…")}</div>
            ) : (<>
              {funnelStages.map((f) => (
                <div key={f.label}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 5 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{f.label}</span>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                      {f.conv ? <span style={{ fontSize: 11, fontWeight: 600, color: f.color, background: alpha(f.color, "1A"), padding: "1px 6px", borderRadius: 999 }}>{f.conv}</span> : null}
                      <span style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>{fmtN(f.count)}</span>
                    </div>
                  </div>
                  <div style={{ height: 14, background: T.n6, borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${barW(f.count)}%`, background: f.color, borderRadius: 4 }} />
                  </div>
                  <div style={{ fontSize: 11, color: T.soft, marginTop: 3 }}>{f.sub}</div>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 2, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ width: 8, height: 8, borderRadius: 999, background: T.orange1 }} /><span style={{ fontSize: 12, color: T.soft }}>{t("dashboard.components.text30", "No-show rate")}</span></div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}><span style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>{noShowRate == null ? "—" : `${noShowRate}%`}</span><span style={{ fontSize: 12, color: T.soft }}>{noShow == null || noShowRate == null ? "—" : t("dashboard.components.noShowsCount.icu", "{count, plural, one {# no-show} other {# no-shows}}", { count: noShow })}</span></div>
              </div>
            </>)}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ── CustomerBehaviorSection ────────────────────────────────────────── */
export function CustomerBehaviorSection(_props: { events: UIEvent[] }) {
  const nav = useNav();
  const t = useT();
  const lbl = useTenantLabels(); // #1 — "Customer" is tenant-overridable vocabulary
  // Walk 2026-10-20 item 2: the noun waits for the tenant's vocabulary; a Skeleton until then, never the default word.
  const nounReady = useTenantLabelsReady();
  // drop24 punt-back: new-vs-returning (59/41 + deltas) and registration channels
  // were hardcoded literals over a real count. Both now bind to real ops —
  // getCustomerBehaviorMix() + getRegistrationChannels() — with honest fallbacks.
  const [mix, setMix] = useState<CustomerBehaviorMixResponse | null>(null);
  const [mixErr, setMixErr] = useState(false);
  const [chans, setChans] = useState<RegistrationChannelsResponse | null>(null);
  const [chansErr, setChansErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (typeof getCustomerBehaviorMix !== "function") { setMixErr(true); return; }
    getCustomerBehaviorMix({ baseUrl: PACING_API_BASE } as never)
      .then((r: unknown) => { if (cancelled) return; const raw = r as Record<string, any>; const p = (raw?.data ?? raw) as CustomerBehaviorMixResponse; if (p && typeof p.newPct === "number") setMix(p); else { console.warn("getCustomerBehaviorMix unexpected shape:", raw); setMixErr(true); } })
      .catch((e: unknown) => { if (!cancelled) { console.warn("getCustomerBehaviorMix failed:", e); setMixErr(true); } });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    let cancelled = false;
    if (typeof getRegistrationChannels !== "function") { setChansErr(true); return; }
    getRegistrationChannels({ baseUrl: PACING_API_BASE } as never)
      .then((r: unknown) => { if (cancelled) return; const raw = r as Record<string, any>; const p = (raw?.data ?? raw) as RegistrationChannelsResponse; if (p && Array.isArray(p.channels)) setChans(p); else { console.warn("getRegistrationChannels unexpected shape:", raw); setChansErr(true); } })
      .catch((e: unknown) => { if (!cancelled) { console.warn("getRegistrationChannels failed:", e); setChansErr(true); } });
    return () => { cancelled = true; };
  }, []);

  // Icon/color are display hints (UI config); the pct/delta DATA comes from the op.
  const CHAN_STYLE: Record<string, { color: string; icon: ReactNode }> = {
    "Direct / web": { color: T.tDarkBlue, icon: <Icon.globe size={13} /> },
    "Email campaigns": { color: T.p1, icon: <Icon.mail size={13} /> },
    "Partner": { color: T.tTurquoise, icon: <Icon.network size={13} /> },
    "Mobile app": { color: T.tOrange, icon: <Icon.phone size={13} /> },
    "Other": { color: T.n3, icon: <Icon.layers size={13} /> },
  };
  const channels = (chans?.channels ?? []).map((c) => ({ label: c.channel, pct: c.pct, deltaPts: c.deltaPts, ...(CHAN_STYLE[c.channel] ?? { color: T.n3, icon: <Icon.layers size={13} /> }) }));
  const chanMax = channels.length ? Math.max(...channels.map((c) => c.pct), 1) : 1;
  const total = mix?.totalRegistrations ?? null;
  const newPct = mix?.newPct ?? null;
  const returnPct = mix?.returnPct ?? null;
  // ui-config — two-row VIEW SCAFFOLD (labels/descs only); every data field
  // (pct/count/delta) computes live from the real registration-mix hook.
  // Marked per Claude Code docket 2026-07-13 (seed-guard false-positive).
  const nvr = [ // ui-config
    { label: t("dashboard.dashboardComponents.newCustomersLabel.icu", "New {value0}", { value0: lbl("customers", t("common.customers", "Customers")).toLowerCase() }), desc: t("dashboard.dashboardComponents.firstRegistration", "First registration"), pct: newPct, count: (total != null && newPct != null) ? Math.round(total * newPct / 100) : null, color: T.tDarkBlue, delta: mix?.newDeltaPts ?? null },
    { label: t("dashboard.dashboardComponents.returning", "Returning"), desc: t("dashboard.dashboardComponents.haveAttendedBefore", "Have attended before"), pct: returnPct, count: (total != null && returnPct != null) ? Math.round(total * returnPct / 100) : null, color: T.p1, delta: mix?.returnDeltaPts ?? null },
  ];

  return (
    <div>
      <SectionHeader title={nounReady ? t("dashboard.components.lblCustomerCustomerBehavior.icu", "{value0} behavior", { value0: lbl("customer", t("common.customer", "Customer")) }) : <Skeleton variant="text" width={160} height={16} />} subtitle={t("dashboard.components.subtitle3", "Across active events · last 30 days")} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("dashboard.components.text31", "New vs returning")}</h3>
            {nounReady
              ? <Btn variant="link" onClick={() => nav.go("reports.guests")}>{t("dashboard.components.byCustomerLink.icu", "By {value0} →", { value0: lbl("customer", t("common.customer", "Customer")).toLowerCase() })}</Btn>
              : <Skeleton variant="text" width={80} />}
          </div>
          <div style={{ padding: "16px 20px" }}>
            {mixErr ? (
              <div style={{ padding: "20px 8px", textAlign: "center", color: T.soft, fontSize: 13 }}>{t("dashboard.components.text33", "Couldn't load the new-vs-returning split.")}</div>
            ) : mix === null || !nounReady ? (
              <div role="status" aria-busy="true" aria-label={t("dashboard.components.text34", "Loading…")} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Skeleton variant="text" width={140} height={24} />
                <Skeleton height={12} />
                {[0, 1].map((i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}><div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}><Skeleton variant="text" width="40%" /><Skeleton variant="text" width="55%" height={10} /></div><Skeleton width={56} height={22} /></div>)}
              </div>
            ) : (<>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 28, fontWeight: 700, color: T.n1, letterSpacing: -0.5 }}>{total != null ? total.toLocaleString() : "—"}</span>
                <span style={{ fontSize: 13, color: T.soft }}>{t("dashboard.components.text35", "total registrations")}</span>
              </div>
              <div style={{ display: "flex", height: 12, borderRadius: 999, overflow: "hidden", gap: 2, marginBottom: 16 }}>
                {[{ pct: newPct ?? 0, color: T.tDarkBlue }, { pct: returnPct ?? 0, color: T.p1 }].map((sg, i) => <div key={i} style={{ width: `${sg.pct}%`, background: sg.color }} />)}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {nvr.map((sg) => (
                  <div key={sg.label} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: sg.color, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{sg.label}</div>
                      <div style={{ fontSize: 12, color: T.soft }}>{sg.desc}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 6, justifyContent: "flex-end" }}>
                        <span style={{ fontSize: 18, fontWeight: 700, color: T.n1 }}>{sg.pct != null ? t("dashboard.components.text39", "{pct}%", { pct: sg.pct }) : "—"}</span>
                        <span style={{ fontSize: 12, color: T.soft }}>{sg.count != null ? sg.count.toLocaleString() : "—"}</span>
                      </div>
                      {sg.delta != null ? <div style={{ fontSize: 11, fontWeight: 600, color: sg.delta >= 0 ? T.green1 : T.red1 }}>{sg.delta >= 0 ? "↑ +" : "↓ "}{Math.abs(sg.delta).toFixed(1)} {t("dashboard.components.pts", "pts")}</div> : null}
                    </div>
                  </div>
                ))}
              </div>
            </>)}
          </div>
        </Card>
        <Card>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("dashboard.components.text36", "Registration channels")}</h3>
            <Btn variant="link" onClick={() => nav.go("reports.events")}>{t("dashboard.components.text27", "Full report →")}</Btn>
          </div>
          <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
            {chansErr ? (
              <div style={{ padding: "20px 8px", textAlign: "center", color: T.soft, fontSize: 13 }}>{t("dashboard.components.text37", "Couldn't load registration channels.")}</div>
            ) : chans === null ? (
              <div style={{ padding: "20px 8px", textAlign: "center", color: T.soft, fontSize: 13 }}>{t("dashboard.components.text34", "Loading…")}</div>
            ) : channels.length === 0 ? (
              <div style={{ padding: "20px 8px", textAlign: "center", color: T.soft, fontSize: 13 }}>{t("dashboard.components.text38", "No channel data yet.")}</div>
            ) : (<>
              {channels.map((c) => (
                <div key={c.label} style={{ display: "grid", gridTemplateColumns: "minmax(96px,128px) minmax(48px,1fr) 78px", alignItems: "center", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <span style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", background: alpha(c.color, "20"), color: c.color }}>{c.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</span>
                  </div>
                  <div style={{ height: 8, background: T.n6, borderRadius: 999, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${(c.pct / chanMax) * 100}%`, background: c.color, borderRadius: 999 }} />
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 5, justifyContent: "flex-end" }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{c.pct}%</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: c.deltaPts >= 0 ? T.green1 : T.red1, width: 40, textAlign: "right" }}>{c.deltaPts >= 0 ? "+" : ""}{c.deltaPts.toFixed(1)}</span>
                  </div>
                </div>
              ))}
            </>)}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ── DashSectionWrap ────────────────────────────────────────────────── */
// ui-raw: the up/down reorder nudge buttons below use this disabled-aware chip
// style (color/opacity/cursor all keyed off `disabled`, no shadow). IconButton's
// fixed style object replaces any caller `style` wholesale — it has no disabled
// dimming of its own and always adds T.shadowChrome — so it can't reproduce this
// disabled-aware, shadowless look.
const dashNudgeStyle = (disabled: boolean): CSSProperties => ({ width: 26, height: 26, borderRadius: 6, border: `1px solid ${T.n4}`, background: T.white, color: disabled ? T.n3 : T.n1, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1 });
export function DashSectionWrap({ id, label, index, total, isDragging, onHandleDown, onMove, innerRef, children }: { id: string; label: string; index: number; total: number; isDragging: boolean; onHandleDown: (id: string) => void; onMove: (id: string, dir: number) => void; innerRef: (el: HTMLDivElement | null) => void; children: ReactNode }) {
  return (
    <div ref={innerRef} style={{ position: "relative", border: `2px dashed ${isDragging ? T.p1 : T.n4}`, borderRadius: 12, padding: 10, background: isDragging ? T.p4 : T.white, boxShadow: isDragging ? T.shadowMd : "none", opacity: isDragging ? 0.92 : 1, transition: "border-color 120ms, background 120ms" }}>
      <div onPointerDown={(e) => { if (e.button === 0) { e.preventDefault(); onHandleDown(id); } }} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, padding: "2px 4px", cursor: "grab", touchAction: "none", userSelect: "none" }}>
        <span style={{ display: "inline-flex", color: T.soft }}><Icon.grip size={16} /></span>
        <span style={{ fontSize: 12, fontWeight: 700, color: T.n1, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
          <button onPointerDown={(e) => e.stopPropagation()} onClick={() => onMove(id, -1)} disabled={index === 0} style={dashNudgeStyle(index === 0)}><Icon.chev dir="up" size={12} /></button>
          <button onPointerDown={(e) => e.stopPropagation()} onClick={() => onMove(id, 1)} disabled={index === total - 1} style={dashNudgeStyle(index === total - 1)}><Icon.chev dir="down" size={12} /></button>
        </div>
      </div>
      <div style={{ pointerEvents: "none" }}>{children}</div>
    </div>
  );
}
